import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import {
  AddCircle,
  Block,
  CheckCircle,
  FileCopy,
  HighlightOff,
  Refresh
} from "@material-ui/icons";
import { api } from "../../services/apiService";
import Menu from "../menu/menu";
import "./absite-program-code-registry.scss";

const statusOptions = ["ALL", "PENDING", "ACTIVE", "INACTIVE", "REJECTED"];

const initialForm = {
  programCode: "",
  programName: "",
  institutionName: "",
  requestorName: "",
  requestorEmail: "",
  status: "ACTIVE",
  notes: ""
};

const emptyCounts = {
  total: 0,
  pending: 0,
  active: 0,
  inactive: 0,
  rejected: 0
};

const extractErrorMessage = (response, fallback) => {
  if (response?.errMsg?.message) {
    return response.errMsg.message;
  }
  if (typeof response?.errMsg === "string") {
    return response.errMsg;
  }
  return fallback;
};

const hasApiError = (response) => Boolean(
  response?.errStatus || typeof response?.status === "number"
);

const displayStatus = (status) => status || "UNKNOWN";

const formatDate = (value) => {
  if (!value) {
    return "Not recorded";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Not recorded";
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const buildDiagnosticLink = (programCode) => {
  const origin = window.location.origin || "";
  return `${origin}/absite-diagnostic?programCode=${encodeURIComponent(programCode)}`;
};

const sanitizeForm = (form) => ({
  programCode: form.programCode.trim(),
  programName: form.programName.trim(),
  institutionName: form.institutionName.trim(),
  requestorName: form.requestorName.trim(),
  requestorEmail: form.requestorEmail.trim(),
  status: form.status,
  notes: form.notes.trim()
});

export default function AbsiteProgramCodeRegistryPage() {
  const isAdmin = window.localStorage.getItem("role") === "ROLE_ADMIN";
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [programCodes, setProgramCodes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const counts = useMemo(() => programCodes.reduce((acc, row) => {
    const status = displayStatus(row.status).toLowerCase();
    return {
      ...acc,
      total: acc.total + 1,
      [status]: acc[status] === undefined ? 1 : acc[status] + 1
    };
  }, emptyCounts), [programCodes]);

  const canCreate = form.programName.trim() && !loading && !savingCode;

  const loadCodes = useCallback(async (nextStatus = statusFilter) => {
    if (!isAdmin) {
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    try {
      const response = await api.listAbsiteProgramCodes(
        nextStatus === "ALL" ? {} : { status: nextStatus }
      );

      if (hasApiError(response)) {
        setError(extractErrorMessage(
          response,
          "Could not load program codes. Please try again."
        ));
        return;
      }

      setProgramCodes(Array.isArray(response) ? response : []);
    } catch {
      setError("Could not load program codes. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, statusFilter]);

  useEffect(() => {
    loadCodes(statusFilter);
  }, [loadCodes, statusFilter]);

  const onFormChange = (field) => (event) => {
    setForm({
      ...form,
      [field]: event.target.value
    });
  };

  const replaceCode = (updatedCode) => {
    setProgramCodes((currentCodes) => {
      const isVisibleInFilter = statusFilter === "ALL"
        || updatedCode.status === statusFilter;
      if (!isVisibleInFilter) {
        return currentCodes.filter(
          (code) => code.programCode !== updatedCode.programCode
        );
      }

      const exists = currentCodes.some(
        (code) => code.programCode === updatedCode.programCode
      );

      if (!exists) {
        return [updatedCode, ...currentCodes];
      }

      return currentCodes.map((code) => (
        code.programCode === updatedCode.programCode ? updatedCode : code
      ));
    });
  };

  const onCreateCode = async (event) => {
    event.preventDefault();
    if (!canCreate) {
      return;
    }

    setSavingCode("create");
    setError("");
    setNotice("");
    try {
      const response = await api.createAbsiteProgramCode(sanitizeForm(form));

      if (hasApiError(response)) {
        setError(extractErrorMessage(
          response,
          "Could not create program code. Please try again."
        ));
        return;
      }

      replaceCode(response);
      setForm(initialForm);
      setNotice(`${response.programCode} created.`);
    } catch {
      setError("Could not create program code. Please try again.");
    } finally {
      setSavingCode("");
    }
  };

  const onUpdateStatus = async (row, status) => {
    setSavingCode(`${row.programCode}:${status}`);
    setError("");
    setNotice("");
    try {
      const response = await api.updateAbsiteProgramCode(row.programCode, { status });

      if (hasApiError(response)) {
        setError(extractErrorMessage(
          response,
          `Could not update ${row.programCode}. Please try again.`
        ));
        return;
      }

      replaceCode(response);
      setNotice(`${row.programCode} updated to ${status}.`);
    } catch {
      setError(`Could not update ${row.programCode}. Please try again.`);
    } finally {
      setSavingCode("");
    }
  };

  const onCopyLink = async (row) => {
    setError("");
    setNotice("");
    try {
      await navigator.clipboard.writeText(buildDiagnosticLink(row.programCode));
      setNotice(`Copied ${row.programCode} diagnostic link.`);
    } catch {
      setError("Could not copy the diagnostic link.");
    }
  };

  if (!isAdmin) {
    return (
      <Box component="div" className="body-cont-over program-code-registry-page">
        <Menu />
        <Box component="main" className="program-code-registry-shell">
          <Paper elevation={0} className="program-code-registry-denied">
            <Typography component="h1">Program Codes</Typography>
            <Typography component="p">Admin access required.</Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="div" className="body-cont-over program-code-registry-page">
      <Menu />

      <Backdrop open={loading || Boolean(savingCode)} className="result-loader">
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box component="main" className="program-code-registry-shell">
        <Box component="header" className="program-code-registry-header">
          <Box component="div">
            <Typography component="h1">Program Codes</Typography>
            <Typography component="p">Internal ABSITE pilot registry</Typography>
          </Box>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => loadCodes(statusFilter)}
            startIcon={<Refresh />}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Box component="div" className="program-code-registry-error" role="alert">
            {error}
          </Box>
        )}

        {notice && (
          <Box component="div" className="program-code-registry-notice">
            {notice}
          </Box>
        )}

        <Box component="section" className="program-code-registry-cards">
          <Paper elevation={0} className="program-code-registry-card">
            <span>Total</span>
            <strong>{counts.total}</strong>
          </Paper>
          <Paper elevation={0} className="program-code-registry-card pending">
            <span>Pending</span>
            <strong>{counts.pending}</strong>
          </Paper>
          <Paper elevation={0} className="program-code-registry-card active">
            <span>Active</span>
            <strong>{counts.active}</strong>
          </Paper>
          <Paper elevation={0} className="program-code-registry-card inactive">
            <span>Inactive</span>
            <strong>{counts.inactive + counts.rejected}</strong>
          </Paper>
        </Box>

        <Paper elevation={0} className="program-code-registry-create">
          <Typography component="h2">Create internal code</Typography>
          <form noValidate onSubmit={onCreateCode} className="program-code-registry-form">
            <TextField
              id="program-code-registry-program-name"
              label="Program name"
              variant="outlined"
              value={form.programName}
              onChange={onFormChange("programName")}
              size="small"
              required
            />
            <TextField
              id="program-code-registry-institution"
              label="Institution"
              variant="outlined"
              value={form.institutionName}
              onChange={onFormChange("institutionName")}
              size="small"
            />
            <TextField
              id="program-code-registry-program-code"
              label="Program code"
              variant="outlined"
              value={form.programCode}
              onChange={onFormChange("programCode")}
              size="small"
            />
            <TextField
              id="program-code-registry-create-status"
              label="Status"
              variant="outlined"
              value={form.status}
              onChange={onFormChange("status")}
              size="small"
              select
              SelectProps={{ native: true }}
            >
              {statusOptions.filter((status) => status !== "ALL").map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </TextField>
            <TextField
              id="program-code-registry-requestor-name"
              label="Requestor name"
              variant="outlined"
              value={form.requestorName}
              onChange={onFormChange("requestorName")}
              size="small"
            />
            <TextField
              id="program-code-registry-requestor-email"
              label="Requestor email"
              variant="outlined"
              type="email"
              value={form.requestorEmail}
              onChange={onFormChange("requestorEmail")}
              size="small"
            />
            <TextField
              id="program-code-registry-notes"
              label="Notes"
              variant="outlined"
              value={form.notes}
              onChange={onFormChange("notes")}
              size="small"
            />
            <Button
              color="primary"
              variant="contained"
              type="submit"
              startIcon={<AddCircle />}
              disabled={!canCreate}
            >
              Create code
            </Button>
          </form>
        </Paper>

        <Paper elevation={0} className="program-code-registry-filters">
          <TextField
            id="program-code-registry-filter-status"
            label="Filter status"
            variant="outlined"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            size="small"
            select
            SelectProps={{ native: true }}
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </TextField>
        </Paper>

        <TableContainer component={Paper} elevation={0} className="program-code-registry-table-wrap">
          <Table className="program-code-registry-table">
            <TableHead>
              <TableRow>
                <TableCell>Program code</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Requestor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programCodes.map((row) => (
                <TableRow key={row.id || row.programCode}>
                  <TableCell className="program-code-registry-code">
                    {row.programCode}
                  </TableCell>
                  <TableCell>
                    <strong>{row.programName || "Unnamed program"}</strong>
                    <span>{row.institutionName || "Institution not recorded"}</span>
                  </TableCell>
                  <TableCell>
                    <strong>{row.requestorName || "Not recorded"}</strong>
                    <span>{row.requestorEmail || "Email not recorded"}</span>
                  </TableCell>
                  <TableCell>
                    <span className={`program-code-status ${displayStatus(row.status).toLowerCase()}`}>
                      {displayStatus(row.status)}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell className="program-code-registry-notes">
                    {row.notes || "No notes"}
                  </TableCell>
                  <TableCell>
                    <Box component="div" className="program-code-registry-actions">
                      <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={() => onCopyLink(row)}
                        startIcon={<FileCopy />}
                        aria-label={`Copy link for ${row.programCode}`}
                      >
                        Copy
                      </Button>
                      {row.status !== "ACTIVE" && (
                        <Button
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => onUpdateStatus(row, "ACTIVE")}
                          startIcon={<CheckCircle />}
                          aria-label={`Activate ${row.programCode}`}
                        >
                          Activate
                        </Button>
                      )}
                      {row.status !== "INACTIVE" && (
                        <Button
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => onUpdateStatus(row, "INACTIVE")}
                          startIcon={<Block />}
                          aria-label={`Deactivate ${row.programCode}`}
                        >
                          Deactivate
                        </Button>
                      )}
                      {row.status !== "REJECTED" && (
                        <Button
                          color="secondary"
                          variant="outlined"
                          size="small"
                          onClick={() => onUpdateStatus(row, "REJECTED")}
                          startIcon={<HighlightOff />}
                          aria-label={`Reject ${row.programCode}`}
                        >
                          Reject
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!programCodes.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box component="div" className="program-code-registry-empty">
                      No program code requests found.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
